import Persona from "persona";
import React, { useRef, useState, useReducer } from "react";
import { bool, func, object, shape, string } from 'prop-types';

import "./PersonaVerification.module.css";
import { useEffect } from "react";
import { updateProfile, updateProfileListing } from "../ProfileSettingsPage/ProfileSettingsPage.duck";
import { compose } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from "react-intl";
import { message } from "antd";
import {
  openWalletVerification,
  updatePersonaEvents,
  sendVerificationStatusEmail,
  savePersonaFieldValues,
  saveKybFieldValues,
} from "../../util/api";
import { safeReturnPath } from "../../util/safeReturnPath";
import { userTypeOf } from "../../util/userType";
import { documentInquiryIdsOf } from "../../util/documentInquiryIds";
import { isEadCategoryCheckEnabled } from "../../util/featureFlags";
import { walletRecordOf } from "../../util/walletRecords";
import { orderRefusalOf, ORDER_STEP_MESSAGE_IDS } from "../../util/documentOrder";
import {
  PERSONA_TEMPLATES,
  governmentIdTemplateFor as governmentIdTemplateForDocument,
  individualPrefillFields,
  businessPrefillFields,
  verificationRequestFor,
} from "../../util/verificationTemplates";

Persona.Client.preload();

// The template rules are shared with the API server. This keeps the page's one-argument form,
// reading the EAD category check from the build configuration.
export const governmentIdTemplateFor = documentName =>
  governmentIdTemplateForDocument(documentName, { eadEnabled: isEadCategoryCheckEnabled() });

// Persona client settings come only from the build configuration. The verification itself (its
// template, prefilled details and reference) is opened by the server, so nothing about it is read
// from the page URL.
export const personaClientOptions = (search, env = process.env) => ({
  environment: env.REACT_APP_PERSONA_ENVIRONMENT || "production",
});

// The listing a Persona check is recorded against. A business check belongs to the company whose
// details the page loaded and prefilled, even when a team member runs it; any other check belongs
// to the person's own listing.
// Why the server would not open a verification, as the message id the page shows.
export const openingRefusalMessageId = error => {
  const status = error?.status;
  const code = error?.code;
  if (status === 402 || code === 'DOCUMENT_NOT_PAID') return 'PersonaVerification.opening.notPaid';
  if (code === 'DOCUMENT_ALREADY_VERIFIED') return 'PersonaVerification.opening.alreadyVerified';
  if (code === 'VERIFICATION_AWAITING_PROVIDER') return 'PersonaVerification.opening.awaitingProvider';
  if (status === 400) return 'PersonaVerification.opening.unknownDocument';
  if (status === 403) return 'PersonaVerification.opening.notAllowed';
  return 'PersonaVerification.opening.unavailable';
};

export const personaSubjectListingId = ({ isKYBFlow, currentUser, profileListing }) => {
  if (isKYBFlow) {
    return profileListing?.id?.uuid;
  }
  const publicData = currentUser?.attributes?.profile?.publicData;
  return publicData?.firstListingId || publicData?.profileListingId;
};

function PersonaVerificationPage(props) {

  const { onUpdateProfile, onUpdateProfileListing, params, currentUser, profileListing, intl } = props;

  const { tab } = params;

  const [options, setOptions] = useState(() => personaClientOptions(window.location.search));

  // Extract isReVerify parameter from URL
  const [isReVerify, setIsReVerify] = useState(() => {
    const qsParams = new URLSearchParams(window.location.search);
    return qsParams.get("isReVerify") === "true";
  });

  const [flowType, setFlowType] = useState("embedded");
  const [status, setStatus] = useState("uninitialized");
  const [showInlineFlow, setShowInlineFlow] = useState(true);
  const [logs, dispatch] = useReducer((currLogs, action) => {
    switch (action.type) {
      case "append":
        return currLogs.concat(action.log);
      case "clear":
        return [];
      default:
        return currLogs;
    }
  }, []);

  const embeddedClientRef = useRef(null);

  /**
   * Helper function to determine document type from URL parameters or fields.
   * This function extracts the actual document ID (for tracking) and name from URL parameters.
   *
   * @returns {string} Document ID (docId parameter) or document name as fallback
   */
  const getDocumentType = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // Priority 1: Use docId (the actual item ID like "both-1", "identity-1", etc.)
    const docId = urlParams.get('docId');
    if (docId) {
      return docId;
    }

    // Priority 2: Fall back to doc name if docId not available (for backward compatibility)
    const docName = urlParams.get('doc');
    if (docName) {
      return decodeURIComponent(docName);
    }

    // Fallback to tab if no parameters provided
    return tab || 'Identity Document';
  };

  /**
   * Helper function to create verification checks for the current document type.
   * This function analyzes the fields returned from Persona verification and creates
   * boolean verification checks only (no field values stored).
   *
   * @param {Object} fields - Fields returned from Persona verification
   * @param {string} documentType - Type of document being verified
   * @param {Object} prefilledFields - Fields that were pre-filled before verification
   * @returns {Object} Boolean verification checks object
   */
  const createVerificationChecks = (fields, documentType, prefilledFields = {}) => {
    const profile = currentUser?.attributes?.profile;
    const publicData = profile?.publicData || {};

    // Determine if this is a KYB flow
    const qsParams = new URLSearchParams(window.location.search);
    const userType = qsParams.get("userType") || "individual";
    const isKYBFlow = userType === "business";

    // Create verification checks for the specific document type (boolean only)
    const verificationChecks = {};
    verificationChecks[documentType] = {
      // Common verification checks for any document
      hasName: false,
      hasBirthDate: false,
      hasAddress: false,
      hasPhone: false,
      hasEmail: false,
      hasSSN: false,
      hasGovernmentId: false,
      hasSelfie: false,
      hasEmploymentInfo: false,
      hasEducationInfo: false,
      hasBusinessInfo: false,
      hasInsuranceInfo: false,

      // Profile matching checks
      nameMatches: false,
      emailMatches: false,
      addressMatches: false,

      // Store all pre-filled field names (for tracking what was auto-populated)
      prefilledFieldNames: Object.keys(prefilledFields),

      // Verification metadata
      isCompleted: false,
      verifiedAt: new Date().toISOString(),
      flowType: isKYBFlow ? 'KYB' : 'KYC',
    };

    // Name verification
    const firstName = fields['name-first']?.value;
    const lastName = fields['name-last']?.value;
    if (firstName && lastName) {
      verificationChecks[documentType].hasName = true;
      const nameMatches = firstName === profile?.firstName && lastName === profile?.lastName;
      verificationChecks[documentType].nameMatches = nameMatches;
    }

    // Birth date verification
    if (fields['birthdate']?.value) {
      verificationChecks[documentType].hasBirthDate = true;
    }

    // Address verification
    const addressFields = {
      street1: fields['address-street-1']?.value,
      city: fields['address-city']?.value,
      state: fields['address-subdivision']?.value,
      postalCode: fields['address-postal-code']?.value,
      country: fields['address-country-code']?.value
    };

    const hasCompleteAddress = addressFields.street1 && addressFields.city &&
      addressFields.state && addressFields.postalCode;

    if (hasCompleteAddress) {
      verificationChecks[documentType].hasAddress = true;
      const cityMatches = addressFields.city?.trim()?.toLowerCase() ===
        publicData?.cityAddress?.trim()?.toLowerCase();
      verificationChecks[documentType].addressMatches = cityMatches;
    }

    // Phone verification
    if (fields['phone-number']?.value) {
      verificationChecks[documentType].hasPhone = true;
    }

    // Email verification
    if (fields['email-address']?.value) {
      verificationChecks[documentType].hasEmail = true;
      const emailMatches = fields['email-address'].value === currentUser?.attributes?.email;
      verificationChecks[documentType].emailMatches = emailMatches;
    }

    // SSN verification
    if (fields['social-security-number']?.value) {
      verificationChecks[documentType].hasSSN = true;
    }

    // Government ID verification
    if (fields['current-government-id']?.value) {
      verificationChecks[documentType].hasGovernmentId = true;
    }

    // Selfie verification
    if (fields['selfie']?.value || fields['face']?.value) {
      verificationChecks[documentType].hasSelfie = true;
    }

    // Employment information
    if (fields['employer']?.value || fields['job-title']?.value ||
      fields['employment-start-date']?.value || fields['annual-income']?.value) {
      verificationChecks[documentType].hasEmploymentInfo = true;
    }

    // Education information
    if (fields['degree']?.value || fields['school']?.value ||
      fields['institution']?.value || fields['graduation-date']?.value) {
      verificationChecks[documentType].hasEducationInfo = true;
    }

    // Business information
    if (fields['business-name']?.value || fields['business-type']?.value ||
      fields['tax-id']?.value || fields['ein']?.value) {
      verificationChecks[documentType].hasBusinessInfo = true;
    }

    // Insurance information
    if (fields['policy-number']?.value || fields['insurance-provider']?.value ||
      fields['coverage-amount']?.value) {
      verificationChecks[documentType].hasInsuranceInfo = true;
    }

    // KYB-specific fields
    if (isKYBFlow) {
      // Business details
      if (fields['business_name']?.value) {
        verificationChecks[documentType].hasBusinessName = true;
      }
      if (fields['business_type']?.value) {
        verificationChecks[documentType].hasBusinessType = true;
      }
      if (fields['business_tax_identification_number']?.value) {
        verificationChecks[documentType].hasTaxId = true;
      }
      if (fields['business_physical_address_street_1']?.value) {
        verificationChecks[documentType].hasBusinessAddress = true;
      }

      // Beneficial owners
      const hasUBO = Object.keys(fields).some(key => key.startsWith('ubo_'));
      if (hasUBO) {
        verificationChecks[documentType].hasBeneficialOwners = true;
      }

      // Control person
      if (fields['control_person_name_first']?.value || fields['control_person_name_last']?.value) {
        verificationChecks[documentType].hasControlPerson = true;
      }

      // Form filler
      if (fields['form_filler_name_first']?.value || fields['form_filler_name_last']?.value) {
        verificationChecks[documentType].hasFormFiller = true;
      }
    }

    // Mark as completed if we have any verification data
    verificationChecks[documentType].isCompleted = true;

    return verificationChecks;
  };

  /**
   * Helper function to merge new verification checks with existing ones.
   * This ensures that verification data accumulates across multiple document completions.
   * 
   * @param {Object} existingChecks - Existing verification checks from user profile
   * @param {Object} newChecks - New verification checks from current document
   * @returns {Object} Merged verification checks object
   */
  const mergeVerificationChecks = (existingChecks = {}, newChecks = {}) => {
    const merged = { ...existingChecks };

    // Merge each document type
    Object.keys(newChecks).forEach(documentType => {
      if (!merged[documentType]) {
        // If document type doesn't exist, add it completely
        merged[documentType] = newChecks[documentType];
      } else {
        // If document exists, merge boolean values (OR operation - once true, stays true)
        const existing = merged[documentType];
        const newDoc = newChecks[documentType];

        merged[documentType] = {
          // Keep existing true values, update with new true values
          hasName: existing.hasName || newDoc.hasName,
          hasBirthDate: existing.hasBirthDate || newDoc.hasBirthDate,
          hasAddress: existing.hasAddress || newDoc.hasAddress,
          hasPhone: existing.hasPhone || newDoc.hasPhone,
          hasEmail: existing.hasEmail || newDoc.hasEmail,
          hasSSN: existing.hasSSN || newDoc.hasSSN,
          hasGovernmentId: existing.hasGovernmentId || newDoc.hasGovernmentId,
          hasSelfie: existing.hasSelfie || newDoc.hasSelfie,
          hasEmploymentInfo: existing.hasEmploymentInfo || newDoc.hasEmploymentInfo,
          hasEducationInfo: existing.hasEducationInfo || newDoc.hasEducationInfo,
          hasBusinessInfo: existing.hasBusinessInfo || newDoc.hasBusinessInfo,
          hasInsuranceInfo: existing.hasInsuranceInfo || newDoc.hasInsuranceInfo,

          // KYB-specific fields
          hasBusinessName: existing.hasBusinessName || newDoc.hasBusinessName,
          hasBusinessType: existing.hasBusinessType || newDoc.hasBusinessType,
          hasTaxId: existing.hasTaxId || newDoc.hasTaxId,
          hasBusinessAddress: existing.hasBusinessAddress || newDoc.hasBusinessAddress,
          hasBeneficialOwners: existing.hasBeneficialOwners || newDoc.hasBeneficialOwners,
          hasControlPerson: existing.hasControlPerson || newDoc.hasControlPerson,
          hasFormFiller: existing.hasFormFiller || newDoc.hasFormFiller,

          // Profile matching checks (update with latest verification)
          nameMatches: newDoc.nameMatches || existing.nameMatches,
          emailMatches: newDoc.emailMatches || existing.emailMatches,
          addressMatches: newDoc.addressMatches || existing.addressMatches,

          // Merge pre-filled field names (keep unique list)
          prefilledFieldNames: Array.from(new Set([
            ...(existing.prefilledFieldNames || []),
            ...(newDoc.prefilledFieldNames || [])
          ])),

          // Update completion status and timestamp
          isCompleted: true,
          verifiedAt: newDoc.verifiedAt, // Use latest timestamp
          flowType: newDoc.flowType || existing.flowType, // Preserve flow type
        };
      }
    });

    return merged;
  };

  /**
   * Asks the server to open (or resume) this document's verification, then opens the provider's
   * flow on the verification it answered. The template, prefilled details and reference are chosen
   * by the server; the page only names the document. When the server refuses, the page says why
   * and goes back to the Wallet.
   *
   * @param {Object} profileListing
   * @param {() => boolean} isCancelled - true once the page has moved on and nothing should open
   */
  const createClient = async (profileListing, isCancelled = () => false) => {
    embeddedClientRef.current?.destroy();
    embeddedClientRef.current = null;

    setStatus("loading");

    // Determine if this is a KYB flow based on URL parameters
    const qsParams = new URLSearchParams(window.location.search);
    const userType = qsParams.get("userType") || "individual";
    const isKYBFlow = userType === "business";
    const docParam = qsParams.get('doc');

    const docId = qsParams.get('docId');
    let opening;
    try {
      opening = await openWalletVerification({
        itemId: docId,
        scope: isKYBFlow ? 'company' : 'self',
      });
    } catch (error) {
      if (isCancelled()) return;
      // Not refused as such: the verification consent comes first, and the Wallet asks for it
      // for this document, then comes back here.
      if (error?.code === 'CONSENT_REQUIRED') {
        setStatus("refused");
        window.location.href = `/wallet?consentFor=${encodeURIComponent(docId || '')}`;
        return;
      }
      setStatus("refused");
      message.error(intl.formatMessage({ id: openingRefusalMessageId(error) }));
      setTimeout(() => {
        window.location.href = '/wallet';
      }, 2000);
      return;
    }
    if (isCancelled()) return;

    // Which template the server opened this document on, for the verification record kept when it
    // completes; the page itself no longer sends a template.
    const { templateId } = verificationRequestFor({
      item: {
        name: docParam ? decodeURIComponent(docParam) : '',
        isKYB: isKYBFlow,
        subject: isKYBFlow ? 'company' : 'self',
      },
      user: currentUser,
      listing: profileListing,
      eadEnabled: isEadCategoryCheckEnabled(),
    });

    const client = new Persona.Client({
      inquiryId: opening?.inquiryId,
      sessionToken: opening?.sessionToken,
      environment: options.environment,
      onLoad: () => {
        setStatus("loaded");
        // If calling client.open() here, widget will open sooner and show a loading state.
        // client.open();
      },
      onReady: () => {
        setStatus("ready");
        client.open();
      },
      onComplete: async ({ inquiryId, status, fields }) => {
        if (fields) {
          // Extract document details (keep existing logic for backward compatibility)
          const personaDocumentDetails = {};
          const allowedKeys = ['current-document', 'current-government-id'];

          Object.keys(fields).forEach((key) => {
            if (allowedKeys.includes(key)) {
              const fieldValue = fields[key]?.value;
              personaDocumentDetails[key] = fieldValue;
            }
          });

          // Get the actual document type name from URL parameters
          const documentType = getDocumentType();

          // Get the prefilled fields that were used for this verification
          const qsParams = new URLSearchParams(window.location.search);
          const userType = qsParams.get("userType") || "individual";
          const isKYBFlow = userType === "business";
          const docParam = qsParams.get('doc');
          const prefilledFields = isKYBFlow
            ? businessPrefillFields(profileListing, currentUser)
            : individualPrefillFields(currentUser, docParam ? decodeURIComponent(docParam) : '');

          // Create verification checks for current document type
          const newVerificationChecks = createVerificationChecks(fields, documentType, prefilledFields);

          // Existing verification checks are kept with the profile listing's wallet records
          // (private data; listings not yet moved still have them in public data).
          const existingVerificationChecks = walletRecordOf(profileListing?.attributes, 'documentVerificationChecks') || {};

          // Merge new checks with existing ones
          const mergedVerificationChecks = mergeVerificationChecks(existingVerificationChecks, newVerificationChecks);

          // Get existing inquiry IDs mapping
          const existingInquiryIds = documentInquiryIdsOf(profileListing) || {};

          // Update inquiry IDs mapping with current document type and inquiry ID
          const updatedInquiryIds = {
            ...existingInquiryIds,
            [documentType]: inquiryId
          };

          // Keep legacy verification checks for backward compatibility
          const verificationIdentityChecks = {
            name: false,
            birthDate: false,
            address: false,
            expiryDate: false,
            ssn: false,
          };

          const firstName = fields['name-first']?.value;
          const lastName = fields['name-last']?.value;
          const profile = currentUser?.attributes?.profile;

          if (
            firstName && lastName &&
            firstName === profile?.firstName &&
            lastName === profile?.lastName
          ) {
            verificationIdentityChecks.name = true;
          }

          if (fields['birthdate']?.value) {
            verificationIdentityChecks.birthDate = true;
          }

          if (fields['social-security-number']?.value) {
            verificationIdentityChecks.ssn = true;
          }

          if (fields['expiration-date']?.value) {
            verificationIdentityChecks.expiryDate = fields['expiration-date'].value;
          }

          const city = fields['address-city']?.value;
          const countryCode = fields['address-country-code']?.value;
          const postalCode = fields['address-postal-code']?.value;
          const street = fields['address-street-1']?.value;
          const publicData = currentUser?.attributes?.profile?.publicData;

          const hasAddress = city && countryCode && postalCode && street;

          if (hasAddress && city?.trim()?.toLowerCase() === publicData?.cityAddress?.trim()?.toLowerCase()) {
            verificationIdentityChecks.address = true;
          }

          // Check if this is a Group C template (DocAI-based template)
          const groupCTemplateIds = Object.values(PERSONA_TEMPLATES).filter(id =>
            id !== PERSONA_TEMPLATES.kyb &&
            id !== PERSONA_TEMPLATES.kyc &&
            id !== PERSONA_TEMPLATES.production &&
            id !== PERSONA_TEMPLATES.identity &&
            id !== PERSONA_TEMPLATES.work &&
            id !== PERSONA_TEMPLATES.education &&
            id !== PERSONA_TEMPLATES.experience &&
            id !== PERSONA_TEMPLATES.insurance
          );
          const usesKycAiTemplate = groupCTemplateIds.includes(templateId);

          const documentCompletionRecord = {
            documentType,
            inquiryId,
            completedAt: new Date().toISOString(),
            fieldsVerified: Object.keys(fields).length,
            // status: (isKYBFlow || usesKycAiTemplate) ? 'completed' : 'approved',
            status: (isKYBFlow || status == 'needs_review') ? 'completed' : 'approved',
            isReVerify: isReVerify, // Track if this is a re-verification
          };

          // Existing completion history is kept on the profile listing too, so each verification adds
          // to it rather than starting it over.
          const existingCompletionHistory = walletRecordOf(profileListing?.attributes, 'documentCompletionHistory') || [];
          const updatedCompletionHistory = [...existingCompletionHistory, documentCompletionRecord];

          // Handle re-verification: Remove old credential from dockIoCredentials
          let updatedDockIoCredentials = walletRecordOf(profileListing?.attributes, 'dockIoCredentials') || [];

          if (isReVerify && updatedDockIoCredentials.length > 0) {

            const urlParamsForDoc = new URLSearchParams(window.location.search);
            let docNameForMatch = urlParamsForDoc.get('doc');
            if (docNameForMatch) {
              docNameForMatch = decodeURIComponent(docNameForMatch);
            }

            // Filter out the credential that matches the current documentType
            const filteredCredentials = updatedDockIoCredentials.filter(
              credential => credential?.documentType !== docNameForMatch
            );

            console.log(`[Re-verification] Removing old credential for documentType: ${documentType}`);
            console.log(`[Re-verification] Credentials before: ${updatedDockIoCredentials.length}, after: ${filteredCredentials.length}`);

            updatedDockIoCredentials = filteredCredentials;
          }

          // Handle re-verification: Remove old document from selectedWalletDocs
          let updatedSelectedWalletDocs = walletRecordOf(profileListing?.attributes, 'selectedWalletDocs') || [];

          if (isReVerify && updatedSelectedWalletDocs.length > 0) {
            // Get document name from URL for matching
            const urlParamsForDoc = new URLSearchParams(window.location.search);
            let docNameForMatch = urlParamsForDoc.get('doc');
            if (docNameForMatch) {
              docNameForMatch = decodeURIComponent(docNameForMatch);
            }

            // Filter out the document that matches both documentType and name
            const filteredDocs = updatedSelectedWalletDocs.filter(
              doc => !(doc?.id === documentType ||
                (docNameForMatch && doc?.name?.toLowerCase().trim() === docNameForMatch?.toLowerCase().trim()))
            );

            console.log(`[Re-verification] Removing old document from selectedWalletDocs: ${documentType}`);
            console.log(`[Re-verification] Selected docs before: ${updatedSelectedWalletDocs.length}, after: ${filteredDocs.length}`);

            updatedSelectedWalletDocs = filteredDocs;
          }

          const actionPayload = {
            listingId: currentUser?.attributes?.profile?.publicData?.firstListingId || currentUser?.attributes?.profile?.publicData?.profileListingId,
            documentVerificationChecks: mergedVerificationChecks,
            personaDocumentDetails: personaDocumentDetails,
            verificationIdentityChecks: verificationIdentityChecks,
            documentInquiryIds: updatedInquiryIds,
            documentCompletionHistory: updatedCompletionHistory,
            dockIoCredentials: updatedDockIoCredentials,
            selectedWalletDocs: updatedSelectedWalletDocs,
          };

          await onUpdateProfileListing(actionPayload);

          const urlParamsLink = new URLSearchParams(window.location.search);

          let documentName = urlParamsLink.get('doc');
          if (documentName) {
            documentName = decodeURIComponent(documentName);
          }

          // Extract expiry date from fields if available
          const expiryDate = fields['expiration-date']?.value || null;

          // Store Persona event in database
          try {
            await updatePersonaEvents({
              userId: currentUser?.id?.uuid,
              userName: `${currentUser?.attributes?.profile?.firstName || ''} ${currentUser?.attributes?.profile?.lastName || ''}`.trim(),
              profileListingId: currentUser?.attributes?.profile?.publicData?.firstListingId || currentUser?.attributes?.profile?.publicData?.profileListingId,
              documentType: documentType,
              documentName: documentName,
              inquiryId: inquiryId,
              isKYBFlow: isKYBFlow,
              usesKycAiTemplate: usesKycAiTemplate,
              status: status,
              isReVerify: isReVerify, // Track if this is a re-verification
              expiryDate: expiryDate, // Include expiry date from document
            });
            console.log('Persona event stored successfully', isReVerify ? '(Re-verification)' : '(Initial verification)');
            if (expiryDate) {
              console.log('Document expiry date:', expiryDate);
            }
          } catch (error) {
            console.error('Error storing Persona event:', error);
            // Don't block the flow if storing the event fails
          }

          // Set when the server refused this document because an earlier one is not yet verified
          let orderRefusalStep = null;

          // Store extracted field values in MongoDB for non-KYB flows
          if (!isKYBFlow) {
            try {
              await savePersonaFieldValues({
                inquiryId,
                documentType,
                documentName,
                status,
                fields,
                profileListingId: currentUser?.attributes?.profile?.publicData?.firstListingId || currentUser?.attributes?.profile?.publicData?.profileListingId,
              });
              console.log('Persona field values stored successfully for document:', documentType);
            } catch (fieldError) {
              console.error('Error storing Persona field values:', fieldError);
              orderRefusalStep = orderRefusalOf(fieldError);
              // Don't block the flow if storing field values fails
            }
          }

          // Store KYB field values in credentialVault when KYB flow is approved
          if (isKYBFlow && (status === 'approved')) {
            try {
              // The server reads the business details from the provider's own record, so the
              // values the flow returned are not sent.
              await saveKybFieldValues({
                inquiryId,
                status,
                profileListingId: personaSubjectListingId({ isKYBFlow, currentUser, profileListing }),
              });
              console.log('KYB field values stored successfully in credential vault');
            } catch (kybError) {
              console.error('Error storing KYB field values:', kybError);
              // Don't block the flow if storing KYB field values fails
            }
          }

          // Send verification status email based on status and userType
          // Sends emails for all statuses: approved, needs_review, failed, etc.
          const profileUserType = userTypeOf(currentUser);

          if (!orderRefusalStep && (profileUserType === 'candidate' || profileUserType === 'customer' || profileUserType === 'agency')) {
            try {
              await sendVerificationStatusEmail({
                email: currentUser?.attributes?.email,
                firstName: currentUser?.attributes?.profile?.firstName,
                documentName: documentName,
                userType: profileUserType,
                status: status, // Pass the actual status from Persona
              });
              console.log(`Verification status email sent to ${profileUserType} - Status: ${status}`);
            } catch (emailError) {
              console.error('Error sending verification status email:', emailError);
              // Don't block the flow if email sending fails
            }
          }

          // Check if we have document info and returnTo URL from the wallet page (for multi-document flow)
          const urlParams = new URLSearchParams(window.location.search);
          const returnTo = urlParams.get('returnTo');
          const docName = urlParams.get('doc');

          if (orderRefusalStep) {
            // Not verified: say which document to verify first instead of announcing completion
            const notVerified = docName
              ? intl.formatMessage({ id: 'DocumentOrder.refusedOnPage' }, { document: decodeURIComponent(docName) })
              : `${intl.formatMessage({ id: 'DocumentOrder.notVerified' })}.`;
            const stepMessageId = ORDER_STEP_MESSAGE_IDS[orderRefusalStep];
            message.error(stepMessageId ? `${notVerified} ${intl.formatMessage({ id: stepMessageId })}` : notVerified);
          } else {
            // Show specific document completion message
            const completionMessage = docName
              ? `${decodeURIComponent(docName)} verification completed!`
              : 'Verification complete!';
            message.success(completionMessage);
          }

          // Add delay to show the completion message before redirecting
          // Only a page on this site is returned to; anything else goes to the Wallet.
          setTimeout(() => {
            window.location.href = safeReturnPath(returnTo, '/wallet?personaComplete=true');
          }, 2000); // 2 second delay to show the message

          // onUpdateProfile({
          //   publicData:{
          //     documentVerificationChecks: mergedVerificationChecks,
          //   },
          //   protectedData: {
          //     // Legacy fields (maintain backward compatibility)
          //     personaDocumentDetails: personaDocumentDetails,
          //     verificationIdentityChecks: verificationIdentityChecks,
          //     // personaFields: fields,

          //     // New document-based verification system
          //     documentInquiryIds: updatedInquiryIds,
          //     documentCompletionHistory: updatedCompletionHistory,
          //     // lastVerificationUpdate: new Date().toISOString(),
          //   }, 

          // }).then(res => {

          // })
        }
        // onCreateCredentials(fields);
        // fetch(`/server-handler?inquiry-id=${inquiryId}`);
        // window.location.href = '/profile-settings';
        // alert(`Complete with status ${status}`);
      },
      onError: (error) => {
        alert(error);
      },
      onEvent: (name, meta) => {
        dispatch({ type: "append", log: { name, meta } });
        if (name == 'exit') {
          message.error('Verification incomplete!');

          // Check if we have a returnTo URL
          const urlParams = new URLSearchParams(window.location.search);
          const returnTo = urlParams.get('returnTo');
          const docId = urlParams.get('docId');

          if (returnTo) {
            // Return to wallet with cancellation status and docId; only a page on this site is
            // returned to, anything else goes to the Wallet.
            let returnUrl = safeReturnPath(returnTo, '/wallet?personaComplete=true')
              .replace('personaComplete=true', 'personaCanceled=true');

            // Preserve docId if present
            if (docId && !returnUrl.includes('docId=')) {
              returnUrl += `&docId=${encodeURIComponent(docId)}`;
            }

            window.location.href = returnUrl;
          } else {
            // Fallback to default behavior
            window.location.href = '/wallet?paymentSuccess=true&tab=wallet';
          }
        }
      },
    });
    embeddedClientRef.current = client;
    client.open();

    window.cancel = (force) => {
      if (client) {
        client.cancel(force);
        setStatus("closed");
      } else {
        alert("Initialize client first");
      }
    };
  };

  useEffect(() => {
    if (profileListing) {

      // Extract document name from URL parameters and show initiation message
      const urlParams = new URLSearchParams(window.location.search);
      const docName = urlParams.get('doc');

      // Log verification type
      console.log(`[PersonaVerification] ${isReVerify ? 'Re-verification' : 'Initial verification'} flow started`);

      if (docName) {
        const decodedDocName = decodeURIComponent(docName);
        const verificationTypeText = isReVerify ? 're-verification' : 'verification';
        message.info(`Starting ${decodedDocName} ${verificationTypeText}...`);
      } else {
        const verificationTypeText = isReVerify ? 're-verification' : 'verification';
        message.info(`Starting document ${verificationTypeText}...`);
      }

      // Delay opening the verification to show the message
      let cancelled = false;
      const timer = setTimeout(() => {
        createClient(profileListing, () => cancelled);
      }, 2000); // 2 second delay

      return () => {
        cancelled = true;
        clearTimeout(timer);
        if (embeddedClientRef.current) {
          embeddedClientRef.current.destroy();
          embeddedClientRef.current = null;
        }
      };
    }
  }, [location.pathname, profileListing]);

  return (
    <>

      <div className="demo-panel">

        {/* {flowType === "embedded" && hasTemplateId && (
          <section>
            <h2>Embedded client</h2>
            <p>Client status: {status}</p>
            {embeddedClientRef.current == null ? (
              <button
                onClick={createClient}
                disabled={status !== "uninitialized"}
              >
                Initialize client
              </button>
            ) : (
              <>
                <button onClick={() => embeddedClientRef.current.open()}>
                  Reopen client
                </button>
                <button onClick={createClient}>Reinitialize client</button>
              </>
            )} */}
        {/* <p>
              Close widget with <code>window.cancel(force?: boolean)</code>.
            </p> */}
        {/* </section> */}
        {/* )} */}

      </div>

      {/* <details className="log-panel">
        <summary className="log-panel__summary">Logs</summary>
        <div className="log-panel__container">
          {logs
            .map(({ name, meta }, idx) => (
              <div className="log" key={idx}>
                <label>[{name}]</label>
                <pre>
                  <code>{JSON.stringify(meta, undefined, 2)}</code>
                </pre>
              </div>
            ))
            .reverse()}
        </div>
      </details> */}
    </>
  );
}
PersonaVerificationPage.propTypes = {

  // onCreateCredentials: func.isRequired,
  onUpdateProfile: func.isRequired,
  onUpdateProfileListing: func.isRequired,
  currentUser: object,
  profileListing: object,
  params: object,

};


const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { profileListing } = state.PersonaVerification;


  return {
    currentUser,
    profileListing,
  };
};

const mapDispatchToProps = dispatch => ({

  // onCreateCredentials: data => dispatch(createCredentials(data)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onUpdateProfileListing: data => dispatch(updateProfileListing(data)),
});


export default PersonaVerificationPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(PersonaVerificationPage);
